import { AssistantMarkdownEditPreview } from "@/components/assistant/AssistantMarkdownEditPreview";
import type { ContractTypePlanDraft } from "@/components/assistant/contractTypeGuidelinesDraft";
import type { PendingGuidelinePdf } from "@/components/assistant/AssistantPlanContext";
import { InstructionWordLimit } from "@/components/form/InstructionWordLimit";
import { PdfDropzone } from "@/components/common/PdfDropzone";
import { SelfControlledInput } from "@/components/assistant/SelfControlledFieldInputs";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import type { ContractTypeGuidelineDocumentRecord } from "@/types";
import { Trash2 } from "lucide-react";

type Props = {
  draft: ContractTypePlanDraft;
  onDraftChange: (draft: ContractTypePlanDraft) => void;
  disabled?: boolean;
  pendingPdfs: PendingGuidelinePdf[];
  onQueuePdf: (payload: { file: File; extractedText: string }) => void;
  onRemovePendingPdf: (key: string) => void;
  savedDocs?: ContractTypeGuidelineDocumentRecord[];
  getAccessToken: () => Promise<string | null>;
  assistantProposalConflict?: boolean;
  onApplyAssistantProposal?: () => void;
  onKeepLocalEdits?: () => void;
  onFoldReferencePdfs?: () => void;
};

export function AssistantContractTypePlanForm({
  draft,
  onDraftChange,
  disabled = false,
  pendingPdfs,
  onQueuePdf,
  onRemovePendingPdf,
  savedDocs = [],
  getAccessToken,
  assistantProposalConflict = false,
  onApplyAssistantProposal,
  onKeepLocalEdits,
}: Props) {
  const patch = (partial: Partial<ContractTypePlanDraft>) =>
    onDraftChange({ ...draft, ...partial });

  const hasReferenceMaterial =
    draft.guidelines.trim().length > 0 || pendingPdfs.length > 0 || savedDocs.length > 0;

  return (
    <div className="space-y-5">
      {assistantProposalConflict ? (
        <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 px-3 py-2.5 text-sm text-[#92400e] dark:text-amber-200">
          <p className="font-medium">The assistant sent an updated proposal.</p>
          <div className="mt-2 flex flex-wrap gap-2">
            <Button type="button" size="xs" variant="surface" onClick={onApplyAssistantProposal}>
              Use assistant version
            </Button>
            <Button type="button" size="xs" variant="ghost" onClick={onKeepLocalEdits}>
              Keep my edits
            </Button>
          </div>
        </div>
      ) : null}

      <section className="space-y-3">
        <h4 className="text-[11px] font-medium uppercase tracking-wide text-[var(--lm-muted)] dark:text-slate-400">
          General information
        </h4>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="plan-ct-name">Name</Label>
            <SelfControlledInput
              id="plan-ct-name"
              placeholder="Master Service Agreement"
              value={draft.name}
              disabled={disabled}
              onValueChange={(name) => patch({ name })}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="plan-ct-short">Short name</Label>
            <SelfControlledInput
              id="plan-ct-short"
              placeholder="e.g. NDA, MSA"
              value={draft.value}
              disabled={disabled}
              onValueChange={(value) => patch({ value })}
            />
          </div>
        </div>
      </section>

      <AssistantMarkdownEditPreview
        label="Contract type guidelines"
        value={draft.guidelines}
        disabled={disabled}
        onChange={(guidelines) => patch({ guidelines })}
      />
      <InstructionWordLimit
        text={draft.guidelines}
        kind="review-guidelines"
        disabled={disabled}
        onSummarized={(summary) => patch({ guidelines: summary })}
      />

      <section className="space-y-2">
        <PdfDropzone
          mode="queue"
          getAccessToken={getAccessToken}
          onQueued={onQueuePdf}
          fieldLabel="Reference PDFs (optional)"
          disabled={disabled}
        />
        <p className="text-xs text-[#576274] dark:text-gray-400">
          Reference PDFs have no word limit. They are searched during the review and are not
          pasted into the guidelines.
        </p>
        {pendingPdfs.length > 0 ? (
          <ul className="space-y-1.5">
            {pendingPdfs.map((p) => (
              <li
                key={p.key}
                className="rounded-md border border-[rgba(15,23,42,0.09)] px-2 py-1.5 text-xs dark:border-white/10"
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="truncate">{p.summarized ? `Summary of ${p.fileName}` : p.fileName}</span>
                  <button
                    type="button"
                    disabled={disabled}
                    className="shrink-0 text-[#576274] hover:text-red-600 dark:text-gray-400"
                    onClick={() => onRemovePendingPdf(p.key)}
                    aria-label={`Remove ${p.fileName}`}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
                {p.summarized ? (
                  <p className="max-h-40 overflow-auto whitespace-pre-wrap">{p.extractedText}</p>
                ) : null}
              </li>
            ))}
          </ul>
        ) : null}
        {savedDocs.length > 0 ? (
          <ul className="space-y-1.5">
            {savedDocs.map((doc) => (
              <li
                key={doc.id}
                className="rounded-md border border-[rgba(15,23,42,0.09)] px-2 py-1.5 text-xs dark:border-white/10"
              >
                <p className="truncate">{doc.file_name}</p>
              </li>
            ))}
          </ul>
        ) : null}
        {!hasReferenceMaterial ? (
          <p className="text-xs text-amber-700 dark:text-amber-300">
            Add guidelines text, at least one reference PDF, or both before Create.
          </p>
        ) : null}
      </section>
    </div>
  );
}
